import { publicEnv } from "@/config/publicEnv";

export type SynapticUser = {
  id: string;
  email: string;
  name?: string;
  nickname?: string;
  picture?: string;
  role?: string;
  roles?: string[];
};

export const synapticIdentityConfig = {
  portalBase: publicEnv.synapticPortalUrl || "https://identity.axiommanifold.com",
  apiBase: publicEnv.synapticApiUrl || "https://identity.axiommanifold.com",
  tenantSlug: publicEnv.synapticTenant || "axiommanifold",
  tokenStorageKey: "jwt",
};

export const navigateTo = (path: string) => {
  if (typeof window !== "undefined") {
    window.location.assign(path);
  }
};

export const buildAuthCallbackUrl = (origin: string, redirectPath: string) => {
  const cleanOrigin = origin.replace(/\/$/, "");
  const callbackUrl = `${cleanOrigin}/auth/callback`;
  const params = new URLSearchParams({ redirect: redirectPath });
  return `${callbackUrl}?${params.toString()}`;
};

export const buildIdentityUrl = (
  action: "login" | "register",
  currentPath: string,
  origin = typeof window !== "undefined" ? window.location.origin : "https://axiommanifold.com",
) => {
  const returnUrl = buildAuthCallbackUrl(origin, currentPath);
  const params = new URLSearchParams({
    tenant: synapticIdentityConfig.tenantSlug,
    returnUrl,
  });

  return `${synapticIdentityConfig.portalBase}/${action}?${params.toString()}`;
};

export const getLoginUrl = (currentPath: string) => buildIdentityUrl("login", currentPath);
export const getRegisterUrl = (currentPath: string) => buildIdentityUrl("register", currentPath);

export const getCallbackToken = (urlStr: string) => {
  try {
    const url = new URL(urlStr);
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
    return hashParams.get("token") ?? url.searchParams.get("token");
  } catch {
    return null;
  }
};

export const storeCallbackToken = (url: string, storage: Pick<Storage, "setItem">) => {
  const token = getCallbackToken(url);
  if (!token) return false;

  storage.setItem(synapticIdentityConfig.tokenStorageKey, token);
  return true;
};

function safeRedirectPath(value: string | null) {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return "/";
  if (value === "/auth/callback" || value.startsWith("/auth/callback?")) return "/";
  return value;
}

/**
 * Completes the identity callback before React Router is created. This keeps
 * the callback-only processing screen out of the render path and lets public
 * landing content paint immediately while the auth profile hydrates.
 */
export const prepareAuthCallback = (urlStr: string, storage: Pick<Storage, "setItem">) => {
  try {
    const url = new URL(urlStr);
    if (url.pathname.replace(/\/$/, "") !== "/auth/callback") return null;

    storeCallbackToken(urlStr, storage);
    return safeRedirectPath(url.searchParams.get("redirect"));
  } catch {
    return null;
  }
};
